import KPI from 'models/kpi/kpi';
import RecurringPattern from 'models/recurringPattern';
import Task from 'models/tasks/task';
import { ExternalCheckSchedule } from './externalCheckSchedule';
import { IntegrationConfigBase } from './integrationConfigBase';
import { IntegrationConnection } from './integrationConnection';
import { IntegrationConnectionTypes } from './integrationConnectionHandler';
import { IntegrationGroup } from './integrationGroup';
import { IntegrationGroups } from './integrationGroupHandler';
import { IntegrationRun } from './integrationRun';
import { IntegrationType } from './integrationType';
import { IntegrationTypeHandler, IntegrationTypes } from './integrationTypeHandler';

export enum IntegrationStates {
  Disabled = 0,
  Enabled = 1,
}

export enum IntegrationDisplayStates {
  Disabled = 0,
  NeedsAttention = 1,
  Active = 2,
}

export enum ConsentStatus {
  Cancelled = 0,
  Granted = 1,
  Error = 2,
}

export class ConsentResult {
  status: ConsentStatus = ConsentStatus.Cancelled;

  integrationTypeId: IntegrationTypes = 0;

  errorDescription: string = '';

  //the connection type the result is for, when the integration type is not known (a GitHub install that returns
  //without its panel hint): Settings > Automation opens the first added type of that connection type
  integrationConnectionTypeId?: IntegrationConnectionTypes;
}

export function getIntegrationBaseColor(darkMode: boolean): string {
  return darkMode ? '#4467da' : '#afc2f5';
}

export class Integration {
  integrationId: number;

  integrationTypeId: IntegrationTypes;

  state: IntegrationStates;

  config: IntegrationConfigBase;

  integrationConnectionId?: number; //undefined for a type without a connection type

  connection?: IntegrationConnection; //the shared authorization, from back-end

  kpiId: number;

  recurrencePattern: RecurringPattern | undefined;

  ownerId?: string;

  ownerRoleId?: string;

  kpi: KPI;

  integrationType?: IntegrationType; // from back-end

  runs: IntegrationRun[];

  integrationGroupId: IntegrationGroups;

  integrationGroup?: IntegrationGroup;

  templateId?: number;

  template?: Task;

  integrationTypeHandler?: IntegrationTypeHandler; // from front-end

  //a SkillControl check (IntegrationTypes.SkillControlCheck, phase 20R2): set by the API on the annual plan behind the
  //preview toggle; read-only here, SkillControl owns its schedule and runs
  externalCheck?: ExternalCheckSchedule;

  static ConsentStateIdentifier = 'evidence';

  constructor() {
    this.integrationId = 0;
    this.kpiId = 0;
    this.kpi = new KPI();
    this.integrationTypeId = IntegrationTypes.NotSet;
    this.state = IntegrationStates.Disabled;
    this.config = new IntegrationConfigBase();
    this.runs = [];
    this.integrationGroupId = IntegrationGroups.NotSet;
  }

  getLastRun(): IntegrationRun | undefined {
    if (this.runs.length > 0) {
      // runs are ordered by date desc from back-end
      return this.runs[0];
    }

    return undefined;
  }

  static getDefaultRecurrencePattern(): RecurringPattern {
    //Once a week on Saturday
    const pattern = new RecurringPattern();
    pattern.initRecurringPatternWeek('Saturday');
    pattern.isActive = false;

    return pattern;
  }

  //A missing connection counts as not authorized: the API links one on create and after a restore.
  isConnectionAuthorized(): boolean {
    return this.connection?.isAuthorized === true;
  }

  getDisplayState(): IntegrationDisplayStates {
    if (this.state === IntegrationStates.Disabled) {
      return IntegrationDisplayStates.Disabled;
    } else if (this.state === IntegrationStates.Enabled) {
      //a SkillControl check has no connection to authorize: SkillControl runs it and pushes the results
      if (this.isConnectionAuthorized() || this.externalCheck) {
        return IntegrationDisplayStates.Active;
      }
    }

    return IntegrationDisplayStates.NeedsAttention;
  }

  clone(): Integration {
    const clone = new Integration();
    clone.integrationId = this.integrationId;
    clone.integrationTypeId = this.integrationTypeId;
    clone.state = this.state;
    clone.config = { ...this.config }; //cannot clone this because this is de-serialized from back-end
    clone.integrationConnectionId = this.integrationConnectionId;
    clone.connection = this.connection?.clone();
    clone.kpiId = this.kpiId;
    clone.recurrencePattern = this.recurrencePattern?.clone();
    clone.ownerId = this.ownerId;
    clone.ownerRoleId = this.ownerRoleId;
    clone.kpi = this.kpi.clone();
    clone.integrationType = this.integrationType; //shallow copy is fine for integrationType because it's static data from back-end and won't be modified
    clone.runs = [...this.runs]; // shallow copy of runs array because runs are not modified
    clone.integrationGroupId = this.integrationGroupId;
    clone.integrationGroup = this.integrationGroup; //read-only subscription state from the API
    clone.templateId = this.templateId;
    clone.template = this.template?.clone();
    clone.integrationTypeHandler = this.integrationTypeHandler; //use the same instance
    clone.externalCheck = this.externalCheck; //read-only state from the API

    return clone;
  }
}
